'use client';

import { publicPath } from '../lib/public-page-metadata';

import React, { createContext, useCallback, useMemo } from 'react';

// Re-export from shared module so existing imports from LanguageContext still work
export { LANGS, LANG_LABELS, DEFAULT_LANG } from '../lib/langs';
export type { Lang } from '../lib/langs';

import { LANGS, DEFAULT_LANG } from '../lib/langs';
import type { Lang } from '../lib/langs';

const NON_DEFAULT_LANGS = LANGS.filter((l) => l !== 'ja');

export interface LanguageContextValue {
  lang: Lang;
  setLang: (newLang: Lang) => void;
  localizedPath: (path: string) => string;
}

export const LanguageContext = createContext<LanguageContextValue>({
  lang: DEFAULT_LANG,
  setLang: () => {},
  localizedPath: (p) => p,
});

/** Strip language prefix from a pathname */
export function stripLangPrefix(pathname: string): string {
  const re = new RegExp(`^/(${NON_DEFAULT_LANGS.join('|')})(/|$)`);
  return pathname.replace(re, '/');
}

/** Extract lang from pathname */
export function langFromPathname(pathname: string): Lang | null {
  const match = pathname.match(new RegExp(`^/(${NON_DEFAULT_LANGS.join('|')})(/|$)`));
  return match ? (match[1] as Lang) : null;
}

interface LanguageProviderProps {
  children: React.ReactNode;
  lang: Lang;
}

/**
 * Next.js App Router version:
 * Receives `lang` as a prop from the [lang]/layout.tsx server component.
 * Uses window.location + pushState for language switching (no React Router dependency).
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children, lang }) => {
  // Persist to localStorage and update <html lang>
  useMemo(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('la-studio-lang', lang);
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const localizedPath = useCallback(
    (path: string): string => {
      return publicPath(lang, path);
    },
    [lang],
  );

  const setLang = useCallback(
    (newLang: Lang) => {
      if (typeof window === 'undefined') return;
      const fromLang =
        (localStorage.getItem('la-studio-lang') as Lang | null) ?? lang;
      localStorage.setItem('la-studio-lang', newLang);
      (window as unknown as {
        gtag?: (cmd: string, name: string, params?: Record<string, unknown>) => void;
      }).gtag?.('event', 'language_switch', {
        from_lang: fromLang,
        to_lang: newLang,
        page_path: window.location.pathname,
      });
      const barePath = stripLangPrefix(window.location.pathname);
      const newPath = publicPath(newLang, barePath);
      window.location.href = newPath + window.location.search + window.location.hash;
    },
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, localizedPath }), [lang, setLang, localizedPath]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
